import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { userDeactivationClient } from "@/api/user-management/userDeactivation";
import { usersDirectoryClient } from "@/api/user-management/usersDirectory.client";
import { ROLES_ACROSS_ENTITIES_FILTER_KEY } from "@/api/user-management/userManagement.queries";
import type { UserListApiRow, UserListRow } from "@/api/user-management/usersDirectory.types";
import { Button } from "@/components/ui/Button/Button";
import { useSwitchOr } from "@/hooks/access/useSwitchOr";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useEntityData, useMyEntities } from "@/hooks/entities/useEntitiesQueries";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useInsideEntity } from "@/hooks/usePlatformView";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import {
  useCancelInvite,
  useStartImpersonation,
} from "@/hooks/user-management/useUserManagementMutations";
import { useUserManagementData } from "@/hooks/user-management/useUserManagementQueries";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Box, Flex, Group, Text, Title } from "@mantine/core";
import { Alert, Badge, Table } from "@onengine/component-library";
import { IconDownload, IconPlus, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useServerTable } from "@/hooks/table/useServerTable";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { actingTenantFor, getLoginSession } from "@/api/access/access.queries";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";
import {
  ChangePasswordModal,
  type ChangePasswordTarget,
} from "@/components/features/user-management/ChangePasswordModal";
import { DeactivateUserModal } from "@/components/features/user-management/UserRecord/UserDeactivation/DeactivateUserModal";
import { ReactivateUserModal } from "@/components/features/user-management/UserRecord/UserDeactivation/ReactivateUserModal";
import { canImpersonateFrom } from "@/components/features/user-management/UserRecord/userRecordFormat";
import { CancelInviteModal } from "@/components/features/user-management/UsersList/CancelInviteModal";
import { useUsersListColumns } from "@/components/features/user-management/UsersList/useUsersListColumns";
import { useUsersListExport } from "@/components/features/user-management/UsersList/useUsersListExport";
import {
  CANCEL_INVITE_FAILED,
  CANCEL_INVITE_NO_ENTITY,
  EXPORT_CSV,
  EXPORT_FAILED_MESSAGE,
  EXPORT_FAILED_TITLE,
  inviteAlreadyAccepted,
  inviteCancelled,
  inviteStatusUnknown,
  NO_USERS_FOUND,
} from "@/components/features/user-management/UsersList/usersListCopy";
import {
  getApiErrorMessage,
  handleApiError,
  handleApiSuccess,
  showErrorAlert,
} from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import styles from "./user-management.module.scss";

export const Route = createFileRoute("/_authenticated/user-management/")({
  component: UserManagement,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const SERVENGINE_LABEL = "ServEngine";

export const getUserMgmtSortByField = (
  columnId: string,
  isServEngineLevel: boolean,
): string | undefined => {
  const fieldMap: Record<string, string> = {
    name: "username",
    permanentRoles: "role",
    // The two list shapes order the entity by different keys: the ServEngine-level list calls it
    // `entity`, the in-entity list `assignedTo`. An unknown key silently falls back to createdAt.
    entity: isServEngineLevel ? "entity" : "assignedTo",
  };
  return fieldMap[columnId];
};

/**
 * The entity picked in the label menu, named as the label menu names it: a super admin by the
 * entity's record (the label menu's own read, so it is already cached), anyone else from their own
 * entities, since only a super admin may read an entity's record.
 */
function usePickedEntityName(entityId: string | null, isSuperAdmin: boolean): string | null {
  const { data: entity } = useEntityData(isSuperAdmin && entityId ? entityId : "");
  const { data: myEntities } = useMyEntities(!isSuperAdmin && entityId !== null);
  if (!entityId) return null;
  return entity?.entityName ?? myEntities?.find((mine) => mine.id === entityId)?.entityName ?? null;
}

function UserManagement() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/user-management/" });
  const {
    isMainAppLogin,
    isEntityLogin,
    userId: currentUserId,
    entityName,
    tenantId,
  } = useActiveSession();
  const { isSuperAdmin, hasAccess } = usePermissions();
  const { edit: canEditUsers, create: legacyUserCreate } = hasAccess({
    module: MODULE_KEYS_MAP.userManagement,
  });
  // UI19: the create asks user_management:create, and every write on a row's menu (Change
  // Password, Cancel Invite, Deactivate, Reactivate) asks user_management:edit; an Entity Admin's
  // bypass counts, as it does on the server.
  const canCreateUsers = useSwitchOr("user_management", "create", !!legacyUserCreate);
  const canWriteUsers = useSwitchOr("user_management", "edit", !!canEditUsers);
  const { mutate: impersonateUser } = useStartImpersonation();
  const [changePasswordTarget, setChangePasswordTarget] = useState<ChangePasswordTarget | null>(
    null,
  );
  const [cancelInviteTarget, setCancelInviteTarget] = useState<UserListRow | null>(null);
  const [deactivateTargetId, setDeactivateTargetId] = useState<string | null>(null);
  const [reactivateTargetId, setReactivateTargetId] = useState<string | null>(null);
  const { mutate: cancelInvite, isPending: isCancellingInvite } = useCancelInvite();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const insideEntity = useInsideEntity();

  // Which of the two lists the endpoint answers with is decided server-side, by exactly this
  // test (`isUnscopedPlatformCaller`, onengine-backend `common/utils/auth.ts`): a super admin who
  // has not narrowed themselves to an entity gets the ServEngine-level people, one row per person
  // per entity; everyone else gets the people of the entity they are in. The sidebar switcher is
  // what moves between the two, and a pick there is an entity login to the backend (its acting
  // gate sets `isEntityLogin`), so the pick counts here as the login does (UI18). `isMainAppLogin`
  // is deliberately not the test — main-app staff who are not super admins still get the
  // in-entity list, and would be labelled and sorted wrong.
  const isServEngineLevel = isSuperAdmin && !insideEntity;
  const pickedEntityId = !isEntityLogin && insideEntity ? entityTenant : null;
  const pickedEntityName = usePickedEntityName(pickedEntityId, isSuperAdmin);
  const levelLabel = isServEngineLevel
    ? SERVENGINE_LABEL
    : pickedEntityId
      ? (pickedEntityName ?? "")
      : (entityName ?? "");

  useBreadcrumb(
    [
      {
        title: "User Management",
        href: "/user-management",
        icon: getRouteIcon(MODULE_KEYS_MAP.userManagement),
      },
    ],
    { listView: "List View" },
  );

  const filterColumns = useMemo<TableFilterColumnConfig[]>(() => {
    const base: TableFilterColumnConfig[] = [
      // These columns text-match the displayed name on the backend (getValue reads
      // `.value`), so send the chip label rather than a resolved id. The same name
      // can map to several rows, which is why the chip checks every matching entry.
      {
        columnId: "name",
        filterByKey: "user",
        storeType: "users",
        valueSource: "label",
        searchParams: { type: "user", fields: ["label"], limit: 10, key: "user" },
      },
      {
        columnId: "permanentRoles",
        filterByKey: "role",
        storeType: "users",
        valueSource: "label",
        searchParams: {
          type: "roles",
          fields: ["label"],
          limit: 10,
          key: isServEngineLevel ? ROLES_ACROSS_ENTITIES_FILTER_KEY : "role",
        },
      },
    ];
    // Only at ServEngine level, where rows span entities and "who is in RP One" is a real
    // question. Inside an entity every row is that entity, and the sidebar switcher is already
    // the entity filter (sketch edge-manager-view). Same source as the Proposals list's filter.
    if (isServEngineLevel) {
      base.push({
        columnId: "entity",
        filterByKey: "tenantId",
        storeType: "entities",
        searchParams: { type: "entity", fields: ["label"], limit: 10, key: "entityName" },
      });
    }
    return base;
  }, [isServEngineLevel]);

  // Stable: `useServerTable` lists it among its `useMemo` dependencies, so a fresh arrow here
  // would rebuild `queryOptions` on every render.
  const getSortByField = useCallback(
    (columnId: string) => getUserMgmtSortByField(columnId, isServEngineLevel),
    [isServEngineLevel],
  );

  const { queryOptions, isPrefsLoading, controlProps, tableKey } = useServerTable({
    // Its own name, not the old list's: the library sorts any column its saved prefs do not name
    // to the end, so prefs saved under the previous column ids would put the row-actions column
    // (the only id the two sets share) in front of every other column.
    viewName: "userManagementUsersList",
    columns: filterColumns,
    getSortByField,
    enableUrlSync: true,
    searchParams,
    defaultStoreType: "users",
  });

  const {
    data: tableData,
    isPending: isUserManagementDataPending,
    isFetching,
    isError,
    refetch,
  } = useUserManagementData({
    page: queryOptions.page.toString(),
    pageSize: queryOptions.pageSize.toString(),
    search: queryOptions.search,
    sortBy: queryOptions.sortBy,
    sortOrder: queryOptions.sortOrder,
    filters: queryOptions.filters,
  });

  const rows = useMemo<UserListRow[]>(() => {
    const apiRows = (tableData?.data ?? []) as UserListApiRow[];
    return apiRows.map((row) => usersDirectoryClient.decorateListUser(row));
  }, [tableData?.data]);

  const handleOpen = useCallback(
    (row: UserListRow) =>
      navigate({ to: "/user-management/$userId", params: { userId: row.userId } }),
    [navigate],
  );

  // UI32: a ServEngine-level row is one person in one company, and the write is made there; the
  // request would otherwise run in the platform company, where a company person is not found.
  const handleChangePassword = useCallback(
    (row: UserListRow) =>
      setChangePasswordTarget({
        id: row.userId,
        name: row.name,
        email: row.email,
        entityId: isServEngineLevel ? row.entityId : null,
      }),
    [isServEngineLevel],
  );

  const handleImpersonate = useCallback(
    (row: UserListRow) => impersonateUser({ userId: row.userId, userName: row.name }),
    [impersonateUser],
  );

  const handleCancelInvite = useCallback((row: UserListRow) => setCancelInviteTarget(row), []);
  const handleDeactivate = useCallback((row: UserListRow) => setDeactivateTargetId(row.userId), []);
  const handleReactivate = useCallback((row: UserListRow) => setReactivateTargetId(row.userId), []);
  const canDeactivate = canWriteUsers && userDeactivationClient.isAvailable;

  // A row that carries no entity id is in the entity the request is answered in: the one picked in
  // the label menu (never while impersonating, which the request does not carry), else the
  // session's own unless the list is the ServEngine-level one.
  const handleConfirmCancelInvite = (target: UserListRow) => {
    const entityId =
      target.entityId ??
      actingTenantFor(entityTenant, getLoginSession()) ??
      (isServEngineLevel ? null : tenantId);
    if (!entityId) {
      showErrorAlert(CANCEL_INVITE_NO_ENTITY);
      setCancelInviteTarget(null);
      return;
    }
    cancelInvite(
      { userId: target.userId, entityId },
      {
        onSuccess: () => handleApiSuccess(inviteCancelled(target.name)),
        onError: (error: unknown) => {
          const code = (error as ApiError)?.response?.data?.error?.code;
          if (code === "INVITE_ACCEPTED") showErrorAlert(inviteAlreadyAccepted(target.name));
          else if (code === "INVITE_STATUS_UNKNOWN")
            showErrorAlert(inviteStatusUnknown(target.name));
          // handleApiError leaves every 409 to the interceptor, which a quiet request skips.
          else if ((error as ApiError)?.response?.status === 409)
            showErrorAlert(getApiErrorMessage(error, CANCEL_INVITE_FAILED));
          else handleApiError(error as ApiError, CANCEL_INVITE_FAILED);
        },
        onSettled: () => setCancelInviteTarget(null),
      },
    );
  };

  const columns = useUsersListColumns({
    filterColumns,
    canImpersonate: canImpersonateFrom({ isSuperAdmin, isMainAppLogin }),
    currentUserId: currentUserId == null ? null : String(currentUserId),
    onEdit: handleOpen,
    onChangePassword: canWriteUsers ? handleChangePassword : undefined,
    onImpersonate: handleImpersonate,
    onCancelInvite: canWriteUsers ? handleCancelInvite : undefined,
    onDeactivate: canDeactivate ? handleDeactivate : undefined,
    onReactivate: canDeactivate ? handleReactivate : undefined,
  });

  const total = tableData?.total ?? 0;

  const { exportUsers, isExporting, exportFailed, dismissExportFailure } = useUsersListExport({
    queryOptions,
    // `levelLabel` is ServEngine at ServEngine level only; a company whose name has not loaded
    // gets no name rather than ServEngine's.
    listName: levelLabel || null,
  });

  const hasUsers = rows.length > 0;
  const hasActiveFilters = queryOptions.filters.length > 0;
  const hasSearchOrFilters = (queryOptions.search ?? "").trim().length > 0 || hasActiveFilters;
  // `total`, not the rows on screen: a saved page number past the end of the list leaves the
  // page empty without the list being empty, and that is not "nobody has been created yet".
  const shouldShowEmptyState =
    total === 0 && !hasSearchOrFilters && !isUserManagementDataPending && !isError;
  // A failed load with nothing cached is not an empty list: the Alert says what happened, and
  // the table would otherwise claim "No users found" (frames 28–29).
  const hasNothingToShow = isError && !hasUsers;

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Group gap="var(--spacing-small)" align="center">
          <Title order={3} className={styles.title}>
            User Management
          </Title>
          {levelLabel && (
            // Outline, not lowContrast: on the grey page well a lowContrast fill barely separates
            // from the background (Jesus, 2026-10-28).
            <Badge size="medium" variant="outline" color={isServEngineLevel ? "info" : "cactus"}>
              {levelLabel}
            </Badge>
          )}
        </Group>

        {canCreateUsers && (
          <Button
            variant="primary"
            iconLeft={<IconPlus size={16} />}
            onClick={() => {
              navigate({ to: "/user-management/add-user" });
            }}
          >
            New User
          </Button>
        )}
      </Group>
      {isError && (
        <Box mb="var(--spacing-large)">
          <Alert
            type="danger"
            variant="strong"
            size="base"
            closable={false}
            title="Could not load users"
            message="Check your connection and try again."
            // Clearing is offered alongside Retry because the table is not rendered here, so a
            // search or filter that is itself what the request chokes on would otherwise have
            // no control left on the page to undo it.
            actions={[
              { label: "Retry", onClick: () => refetch(), iconLeft: <IconRefresh size={16} /> },
              ...(hasSearchOrFilters
                ? [
                    {
                      label: "Clear Filters",
                      onClick: () => {
                        controlProps.onSearch("");
                        controlProps.onFilter({});
                      },
                    },
                  ]
                : []),
            ]}
          />
        </Box>
      )}
      {exportFailed && (
        <Box mb="var(--spacing-large)">
          <Alert
            type="danger"
            variant="strong"
            size="base"
            closable
            onClose={dismissExportFailure}
            title={EXPORT_FAILED_TITLE}
            message={EXPORT_FAILED_MESSAGE}
          />
        </Box>
      )}
      {hasNothingToShow ? null : shouldShowEmptyState ? (
        <Box>
          <Box className={styles.noUsersContainer}>
            <Title order={6} className={styles.noUsersTitle}>
              No user profiles have been created
            </Title>
            {canCreateUsers && (
              <>
                <Text>
                  You can create a new user profile by clicking on &quot;New User&quot; below
                </Text>
                {/* The margin sits on a wrapper, never on the Button: `ui/Button` spreads `rest`
                    after its own className, so passing one strips every style it has. */}
                <Box className={styles.noUsersAction}>
                  <Button
                    variant="primary"
                    size="large"
                    iconLeft={<IconPlus size={16} />}
                    onClick={() => {
                      navigate({ to: "/user-management/add-user" });
                    }}
                  >
                    New User
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Table<UserListRow>
          key={tableKey}
          {...controlProps}
          columns={columns}
          data={rows}
          // The whole row opens the person; the row menu keeps the actions that are not "open"
          // (Jesus, 2026-10-28). `cellClickIgnoreColumns` from `controlProps` keeps the ⋯ cell out.
          onRowClick={handleOpen}
          loading={isFetching || isPrefsLoading || isUserManagementDataPending}
          totalCount={total}
          // A page that is just a table stops 24px above the bottom and scrolls its rows with the
          // header pinned, rather than scrolling the page (Brain team-assignments §17).
          fillHeight
          actions={[
            {
              label: EXPORT_CSV,
              icon: <IconDownload size={16} />,
              disabled: !hasUsers || isExporting,
              onClick: () => void exportUsers(),
            },
            // `controlProps` carries Reset Columns, the only way back from a column resize; its
            // own comment asks any screen with actions of its own to merge rather than replace.
            ...(controlProps.actions ?? []),
          ]}
          emptyText={NO_USERS_FOUND}
        />
      )}
      <ChangePasswordModal
        target={changePasswordTarget}
        onClose={() => setChangePasswordTarget(null)}
      />
      <CancelInviteModal
        target={cancelInviteTarget}
        onClose={() => setCancelInviteTarget(null)}
        isConfirming={isCancellingInvite}
        onConfirm={handleConfirmCancelInvite}
      />
      <DeactivateUserModal
        userId={deactivateTargetId}
        onClose={() => setDeactivateTargetId(null)}
      />
      <ReactivateUserModal
        userId={reactivateTargetId}
        onClose={() => setReactivateTargetId(null)}
      />
    </Flex>
  );
}
